// @ts-check
// Configurações > Auditoria: quem acessou ou alterou dados, o quê e quando (LGPD), com filtro por tipo de ação
// e paginação. Só administradores.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from '../settings/settings-pages.js';
import { hasCurrentPermissionFlag, objectsByNameSingular } from '../../data/metadata-store.js';
import { fetchAuditLog } from '../../data/operations.js';
import { Link } from '../../app/router.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { formatDateTime } from '../../lib/format.js';
import { auditActionLabel, auditDetailsText } from '../../lib/audit-labels.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';

/** @typedef {import('../../data/operations.js').AuditEntry} AuditEntry */

// filtros pelo prefixo da ação (a API aceita "record." para todas as ações de registro)
const ACTION_FILTERS = Object.freeze([
  ['all', ''], ['records', 'record.'], ['views', 'record.viewed'], ['auth', 'auth.'], ['privacy', 'privacy.'], ['support', 'support.'],
]);
const PAGE_SIZE = 50;

/** @param {{ entry: AuditEntry }} props */
function AuditTarget({ entry }) {
  if (!entry.objectNameSingular || !entry.recordId) return html`<span class="settings-table-muted">-</span>`;
  const objectMetadata = objectsByNameSingular.value.get(entry.objectNameSingular);
  const objectLabel = objectMetadata ? translateMetadataLabel(objectMetadata.labelSingular) : entry.objectNameSingular;
  const label = entry.recordLabel ?? entry.recordId.slice(0, 8);
  return html`<span class="audit-target">
    <span class="settings-table-muted">${objectLabel}</span>
    ${objectMetadata && !objectMetadata.isSystem && entry.objectNameSingular !== 'workspaceMember' && !entry.action.endsWith('.destroyed')
      ? html`<${Link} href=${`/object/${entry.objectNameSingular}/${entry.recordId}`} className="operations-link">${label}<//>`
      : html`<span>${label}</span>`}
  </span>`;
}

export function SettingsAuditPage() {
  const [filterKey, setFilterKey] = useState('all');
  const [entries, setEntries] = useState(/** @type {AuditEntry[] | null} */ (null));
  const [endCursor, setEndCursor] = useState(/** @type {string | null} */ (null));
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // auditoria é política de segurança da instituição (flag SECURITY do papel)
  const isAdmin = hasCurrentPermissionFlag('SECURITY');
  const action = ACTION_FILTERS.find(([key]) => key === filterKey)?.[1] ?? '';

  useEffect(() => {
    document.title = `${t('operations.audit.title')} - ${t('navigation.settings')}`;
  }, []);
  useEffect(() => {
    if (!isAdmin) return;
    setEntries(null);
    fetchAuditLog({ action: action || undefined, limit: PAGE_SIZE })
      .then((page) => {
        setEntries(page.entries);
        setEndCursor(page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null);
      })
      .catch((error) => {
        showErrorSnackbar(error);
        setEntries([]);
      });
  }, [action, isAdmin]);

  const loadMore = async () => {
    if (!endCursor) return;
    setIsLoadingMore(true);
    try {
      const page = await fetchAuditLog({ action: action || undefined, limit: PAGE_SIZE, startingAfter: endCursor });
      setEntries([...(entries ?? []), ...page.entries]);
      setEndCursor(page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const header = html`<${PageHeader} icon="IconHistory" title=${t('operations.audit.title')} />`;
  if (!isAdmin) {
    return html`<${PageCard} header=${header}><div class="settings-page">
      <p class="channel-card-note"><${Icon} name="IconLock" size=${14} />${t('operations.audit.adminOnly')}</p>
    </div><//>`;
  }
  return html`<${PageCard} header=${header}>
    <div class="settings-page settings-page-wide">
      <${SettingsSection} title=${t('operations.audit.title')} description=${t('operations.audit.description')}>
        <div class="audit-filters" role="tablist">
          ${ACTION_FILTERS.map(([key]) => html`<button key=${key} type="button" role="tab" class="audit-filter" aria-selected=${filterKey === key ? 'true' : 'false'}
            onClick=${() => setFilterKey(key)}>${t(`operations.audit.filters.${key}`)}</button>`)}
        </div>
        ${entries === null
          ? html`<${Skeleton} height=${200} radius=${8} />`
          : entries.length === 0
            ? html`<${EmptyState} icon="IconHistory" title=${t('operations.audit.empty')} />`
            : html`<div class="settings-table">
                <div class="settings-table-row settings-table-header audit-row">
                  <span>${t('operations.audit.columns.when')}</span>
                  <span>${t('operations.audit.columns.who')}</span>
                  <span>${t('operations.audit.columns.action')}</span>
                  <span>${t('operations.audit.columns.target')}</span>
                  <span>${t('operations.audit.columns.fields')}</span>
                </div>
                ${entries.map((entry) => html`<div key=${entry.id} class="settings-table-row audit-row">
                  <span class="settings-table-muted">${formatDateTime(entry.createdAt)}</span>
                  <span class="audit-actor">
                    <span class="audit-actor-name">${entry.actorType === 'SYSTEM' || !entry.actorName ? t('operations.audit.system') : entry.actorName}</span>
                    ${entry.actorType === 'SUPPORT' || entry.actorType === 'API' ? html`<span class="audit-actor-type" data-type=${entry.actorType}>${t(`operations.audit.actorTypes.${entry.actorType}`)}</span>` : null}
                  </span>
                  <span>${auditActionLabel(entry)}</span>
                  <${AuditTarget} entry=${entry} />
                  ${entry.updatedFields?.length
                    ? html`<span class="settings-table-muted" title=${auditDetailsText(entry)}>${auditDetailsText(entry)}</span>`
                    : html`<span class="settings-table-muted audit-details">${auditDetailsText(entry)}</span>`}
                </div>`)}
              </div>`}
        ${endCursor
          ? html`<div><${Button} title=${t('operations.audit.loadMore')} variant="secondary" size="small" isLoading=${isLoadingMore} onClick=${loadMore} /></div>`
          : null}
      <//>
    </div>
  <//>`;
}
